import { useHydratedExperimentVariant } from 'src/utils/hydrated-experiment'
import { Experiments } from '../constants'

type Variant = 'control' | 'treatment'

const EXPERIMENT_KEY = Experiments.HomeInputboxRefresh

/**
 * SSR-safe variant lookup.
 *
 * Originally implemented with `useExperimentVariant`, which reads from
 * the Amplitude SDK's client-side warm cache + `localStorage` and
 * returns the `defaultValue` ('control') during SSR. On a logged-in
 * browser the Amplitude SDK warm-cache hit produces 'treatment' on
 * the very first hydrate render, while the server already shipped
 * 'control' in the SSR HTML — the resulting form-tree branches
 * (renders `<PromptsCompletionSwitch>` wrapper / generateButton
 * differently) tripped a hard hydration mismatch on `/`. React 19
 * recovered by discarding the SSR DOM and re-running the whole
 * homepage tree, which is what the user perceived as the visible
 * "loading flash" on first paint.
 *
 * `useHydratedExperiment` reads from the server-injected
 * `window.__HYDRATED_EXPERIMENTS__` snapshot (populated by
 * `experimentMiddleware` -> root loader -> `<HydratedExperimentProvider>`,
 * see PR #10120 + `src/utils/hydrated-experiment.tsx`), so SSR and
 * the first hydrate commit see the exact same variant value — no
 * tearing, no recovery rerender, no flash.
 */
export function useHomeInputboxRefreshExperiment() {
  const { value: variant } = useHydratedExperimentVariant<Variant>(
    EXPERIMENT_KEY,
    'control',
  )

  return {
    variant,
    isTreatment: variant === 'treatment',
  }
}
